/** Browser-safe presentation rules shared by the CLI and web chat.
 *
 * This module is deliberately importless: it is runtime code compiled by both the daemon's NodeNext
 * toolchain and the web's Bundler toolchain. Keep it pure and free of platform APIs.
 */

/** Order stamped deliveries by their actual completion, keeping all other segments in place.
 * Historical unstamped payloads retain their recorded positions. Tool rows always retain start order.
 * A late code-mode result may rewrite an earlier reporting entry, so a run also applies this after
 * collecting deliveries across all its steps. */
export function orderDeliveries<T extends { kind: string; deliveredAt?: number }>(segments: readonly T[]): T[] {
  const ordered = segments.filter(segment => segment.deliveredAt !== undefined)
    .sort((a, b) => a.deliveredAt! - b.deliveredAt!);
  let index = 0;
  return segments.map(segment => segment.deliveredAt !== undefined ? ordered[index++]! : segment);
}

export type ComposeLocale = 'en' | 'cs' | 'sk';

/** Narrative lock hint shared by CLI and web. The full sentence is also the accessible description. */
export function childReadOnlyText(
  locale: ComposeLocale,
  child: { agentType?: string; status: 'running' | 'done' | 'error' },
): { label: string; description: string } {
  const type = child.agentType ? ` ${child.agentType}` : '';
  const outcomes = {
    en: { running: 'is working', done: 'has finished', error: 'ended with an error' },
    cs: { running: '', done: 'už skončil', error: 'skončil s chybou' },
    sk: { running: '', done: 'už skončil', error: 'skončil s chybou' },
  };
  const label = locale === 'en'
    ? `You can't write here: ${child.agentType || child.status !== 'running' ? 'the' : 'a'}${type} sub-agent ${outcomes.en[child.status]}.`
    : child.status === 'running'
      ? `${locale === 'cs' ? 'Sem nemůžete psát' : 'Sem nemôžete písať'}, pracuje tu sub-agent${type}.`
      : `${locale === 'cs' ? 'Sem nemůžete psát' : 'Sem nemôžete písať'}, sub-agent${type} ${outcomes[locale][child.status]}.`;
  return { label, description: label };
}


/** The todo plugin's card id — the ONE card whose rows are the conversation's task list.
 *
 * Every surface that treats a card as TASKS matches on this and nothing else: the CLI's clickable rows,
 * the web rail's Tasks section, the transcript card the rail takes over. Card items are a generic
 * mechanism, so another plugin may well emit rows carrying ids of its own — and those ids are its
 * handles, not task handles the todo API would accept. Matching on "has items with ids" would send them
 * to it anyway. */
export const TODO_CARD_ID = 'todos';
export const TODO_PREVIEW_ITEMS = 4;
export const DEFAULT_COMPOSE_MARKER_MS = 10_000;
export const DEFAULT_LONG_TOOL_COMPOSE_MARKER_MS = 3_000;

/** Chat event kinds that change what the status row shows: a setting, or a delegated job's finish. A
 *  surface refetches its settled status when a live `chat-event` frame of one of these arrives. */
const STATUS_CHANGING_EVENT_KINDS: ReadonlySet<string> = new Set([
  'model', 'mode', 'plan', 'reasoning', 'rename', 'cwd', 'subagent', 'workflow',
]);

/** Whether a live `chat-event` changes what a surface's settled status row shows: the kinds above, plus the
 *  outcome of `/fast`, the one command whose result the status carries (the FAST tag of the model). The web
 *  and the CLI refetch their status on exactly this rule. */
export const changesChatStatus = (details: { kind: string; outcome?: { ok: boolean; code?: string } }): boolean =>
  STATUS_CHANGING_EVENT_KINDS.has(details.kind)
  || (details.kind === 'command' && details.outcome?.ok === true && details.outcome.code === 'fast');

interface LocalePhrase {
  readonly withDetail: (detail: string) => string;
  readonly nameOnly: string;
}
interface Phrase {
  readonly reduce?: (detail: string) => string;
  readonly en: LocalePhrase;
  readonly cs: LocalePhrase;
  readonly sk: LocalePhrase;
}

const MAX_DETAIL_WORDS = 2;

function hostOf(url: string): string {
  const s = url.trim();
  try { return new URL(s).host || s; } catch { /* not a full URL */ }
  const m = /^(?:[a-z][a-z0-9+.-]*:\/\/)?([^/\s?#]+)/i.exec(s);
  return m?.[1] ?? s;
}

function fileOf(detail: string): string {
  const s = detail.trim();
  if (s.length <= 40) return s;
  const slash = s.lastIndexOf('/');
  const base = slash >= 0 ? s.slice(slash + 1) : s;
  return base.length + 2 <= 40 ? `…/${base}` : `…${base.slice(-(40 - 1))}`;
}

function clampDetail(detail: string): string {
  const words = detail.trim().split(/\s+/).filter(Boolean).slice(0, MAX_DETAIL_WORDS).join(' ');
  return words.replace(/[…\s]+$/u, '');
}

const LONG_TOOLS: Readonly<Record<string, Phrase>> = {
  Write: { reduce: fileOf,
    en: { withDetail: (d) => `Writing file ${d}…`, nameOnly: 'Writing file…' },
    cs: { withDetail: (d) => `Píšu soubor ${d}…`, nameOnly: 'Píšu soubor…' },
    sk: { withDetail: (d) => `Píšem súbor ${d}…`, nameOnly: 'Píšem súbor…' } },
  Edit: { reduce: fileOf,
    en: { withDetail: (d) => `Editing file ${d}…`, nameOnly: 'Editing file…' },
    cs: { withDetail: (d) => `Upravuji soubor ${d}…`, nameOnly: 'Upravuji soubor…' },
    sk: { withDetail: (d) => `Upravujem súbor ${d}…`, nameOnly: 'Upravujem súbor…' } },
  Bash: {
    en: { withDetail: (d) => `Running command ${d}…`, nameOnly: 'Running command…' },
    cs: { withDetail: (d) => `Spouštím příkaz ${d}…`, nameOnly: 'Spouštím příkaz…' },
    sk: { withDetail: (d) => `Spúšťam príkaz ${d}…`, nameOnly: 'Spúšťam príkaz…' } },
  Delegate: {
    en: { withDetail: () => 'Starting sub-agent…', nameOnly: 'Starting sub-agent…' },
    cs: { withDetail: () => 'Spouštím sub-agenta…', nameOnly: 'Spouštím sub-agenta…' },
    sk: { withDetail: () => 'Spúšťam sub-agenta…', nameOnly: 'Spúšťam sub-agenta…' } },
  WorkflowStart: {
    en: { withDetail: () => 'Starting workflow…', nameOnly: 'Starting workflow…' },
    cs: { withDetail: () => 'Spouštím workflow…', nameOnly: 'Spouštím workflow…' },
    sk: { withDetail: () => 'Spúšťam workflow…', nameOnly: 'Spúšťam workflow…' } },
  WorkflowAddNodes: {
    en: { withDetail: () => 'Adding nodes…', nameOnly: 'Adding nodes…' },
    cs: { withDetail: () => 'Přidávám uzly…', nameOnly: 'Přidávám uzly…' },
    sk: { withDetail: () => 'Pridávam uzly…', nameOnly: 'Pridávam uzly…' } },
  GenerateImage: {
    en: { withDetail: () => 'Generating image…', nameOnly: 'Generating image…' },
    cs: { withDetail: () => 'Generuji obrázek…', nameOnly: 'Generuji obrázek…' },
    sk: { withDetail: () => 'Generujem obrázok…', nameOnly: 'Generujem obrázok…' } },
  EditImage: {
    en: { withDetail: () => 'Generating image…', nameOnly: 'Generating image…' },
    cs: { withDetail: () => 'Generuji obrázek…', nameOnly: 'Generuji obrázek…' },
    sk: { withDetail: () => 'Generujem obrázok…', nameOnly: 'Generujem obrázok…' } },
  CreateSkill: { reduce: fileOf,
    en: { withDetail: (d) => `Creating skill ${d}…`, nameOnly: 'Creating skill…' },
    cs: { withDetail: (d) => `Vytvářím dovednost ${d}…`, nameOnly: 'Vytvářím dovednost…' },
    sk: { withDetail: (d) => `Vytváram zručnosť ${d}…`, nameOnly: 'Vytváram zručnosť…' } },
  ScanCode: { reduce: fileOf,
    en: { withDetail: (d) => `Scanning code ${d}…`, nameOnly: 'Scanning code…' },
    cs: { withDetail: (d) => `Kontroluji kód ${d}…`, nameOnly: 'Kontroluji kód…' },
    sk: { withDetail: (d) => `Kontrolujem kód ${d}…`, nameOnly: 'Kontrolujem kód…' } },
  WebFetch: { reduce: hostOf,
    en: { withDetail: (d) => `Fetching ${d}…`, nameOnly: 'Fetching…' },
    cs: { withDetail: (d) => `Načítám ${d}…`, nameOnly: 'Načítám…' },
    sk: { withDetail: (d) => `Načítavam ${d}…`, nameOnly: 'Načítavam…' } },
  WebSearch: {
    en: { withDetail: (d) => `Searching ${d}…`, nameOnly: 'Searching…' },
    cs: { withDetail: (d) => `Hledám ${d}…`, nameOnly: 'Hledám…' },
    sk: { withDetail: (d) => `Hľadám ${d}…`, nameOnly: 'Hľadám…' } },
  // Code mode's script tool. Its label is normally the note of the nested call being written (read by the
  // code-mode plugin); this is what shows while that call has none.
  exec: {
    en: { withDetail: () => 'Running tools…', nameOnly: 'Running tools…' },
    cs: { withDetail: () => 'Spouštím nástroje…', nameOnly: 'Spouštím nástroje…' },
    sk: { withDetail: () => 'Spúšťam nástroje…', nameOnly: 'Spúšťam nástroje…' } },
};

export const LONG_COMPOSE_TOOLS: ReadonlySet<string> = new Set(Object.keys(LONG_TOOLS));

/** Localized fallback projection exercised directly by compose-label tests. @internal */
export function composeLabel(name: string | undefined, detail: string | undefined, locale: ComposeLocale): string | undefined {
  if (!name) return undefined;
  const phrase = LONG_TOOLS[name];
  if (!phrase) return undefined;
  const loc = phrase[locale];
  const raw = detail?.trim();
  if (!raw) return loc.nameOnly;
  const reduced = clampDetail(phrase.reduce ? phrase.reduce(raw) : raw);
  return reduced ? loc.withDetail(reduced) : loc.nameOnly;
}

export function composingLabel(
  reason: string | undefined, name: string | undefined, detail: string | undefined, locale: ComposeLocale,
): string | undefined {
  const r = reason?.trim();
  if (r) return r;
  return composeLabel(name, detail, locale);
}

export interface TodoPreviewItem {
  readonly status?: 'pending' | 'in_progress' | 'completed';
}

/** Pick recent progress plus the work that matters next, then restore source order. */
export function todoPreviewItems<T extends TodoPreviewItem>(items: readonly T[], limit: number): T[] {
  if (limit <= 0) return [];
  if (items.length <= limit) return [...items];
  const completed = items.map((item, index) => ({ item, index }))
    .filter(({ item }) => item.status === 'completed');
  const remaining = items.map((item, index) => ({ item, index }))
    .filter(({ item }) => item.status !== 'completed');

  let completedCount = Math.min(2, completed.length, limit);
  let remainingCount = Math.min(2, remaining.length, limit - completedCount);
  let spare = limit - completedCount - remainingCount;
  const extraRemaining = Math.min(spare, remaining.length - remainingCount);
  remainingCount += extraRemaining;
  spare -= extraRemaining;
  completedCount += Math.min(spare, completed.length - completedCount);

  const selectedCompleted = completed.slice(-completedCount);
  const selectedRemaining = [...remaining]
    .sort((a, b) => {
      const activeDelta = Number(b.item.status === 'in_progress') - Number(a.item.status === 'in_progress');
      return activeDelta || a.index - b.index;
    })
    .slice(0, remainingCount);

  return [...selectedCompleted, ...selectedRemaining]
    .sort((a, b) => a.index - b.index)
    .map(({ item }) => item);
}
